/**
 * PASS RATE GAUGE
 *
 * Circular progress gauge showing pass rate percentage.
 * Color changes based on threshold: red (<50%), yellow (50-80%), green (>80%).
 *
 * Uses Recharts RadialBarChart for the gauge visualization.
 */

import React, { useMemo } from 'react'
import { RadialBarChart, RadialBar, ResponsiveContainer } from 'recharts'
import { token } from '@atlaskit/tokens'

/**
 * Get color based on pass rate threshold
 *
 * @param {number} passRate - Pass rate percentage (0-100)
 * @returns {string} Color value
 */
const getPassRateColor = (passRate) => {
  if (passRate >= 80) {
    return token('color.background.success.bold', '#22A06B')
  } else if (passRate >= 50) {
    return token('color.background.warning.bold', '#FF991F')
  } else {
    return token('color.background.danger.bold', '#DE350B')
  }
}

/**
 * Get status text based on pass rate threshold
 *
 * @param {number} passRate - Pass rate percentage (0-100)
 * @returns {string} Status description
 */
const getPassRateStatus = (passRate) => {
  if (passRate >= 80) {
    return 'Good'
  } else if (passRate >= 50) {
    return 'Needs Attention'
  } else {
    return 'Critical'
  }
}

/**
 * PassRateGauge Component
 *
 * @param {object} props
 * @param {number} props.passRate - Pass rate percentage (0-100)
 * @param {number} props.size - Gauge size in pixels (default: 200)
 * @param {boolean} props.showStatus - Whether to show status text (default: true)
 * @param {string} props.label - Custom label below percentage (default: "Pass Rate")
 */
const PassRateGauge = ({
  passRate = 0,
  size = 200,
  showStatus = true,
  label = 'Pass Rate'
}) => {
  // Ensure passRate is within valid range and properly formatted
  const normalizedRate = useMemo(() => {
    const rate = Math.max(0, Math.min(100, passRate))
    return Math.round(rate * 100) / 100 // Round to 2 decimal places
  }, [passRate])

  // Chart data - Recharts RadialBar needs array format
  const chartData = useMemo(() => {
    return [
      {
        name: 'Pass Rate',
        value: normalizedRate,
        fill: getPassRateColor(normalizedRate)
      }
    ]
  }, [normalizedRate])

  // Background track data
  const backgroundData = useMemo(() => {
    return [
      {
        name: 'Background',
        value: 100,
        fill: token('color.background.neutral', '#DFE1E6')
      }
    ]
  }, [])

  const color = getPassRateColor(normalizedRate)
  const status = getPassRateStatus(normalizedRate)

  return (
    <div
      style={{
        width: size,
        height: size + 40, // Extra height for label
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      {/* Chart container */}
      <div style={{ width: size, height: size, position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="70%"
            outerRadius="100%"
            barSize={12}
            data={backgroundData}
            startAngle={90}
            endAngle={-270}
          >
            <RadialBar
              dataKey="value"
              cornerRadius={6}
              background={false}
            />
          </RadialBarChart>
        </ResponsiveContainer>

        {/* Foreground chart (actual value) */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="70%"
              outerRadius="100%"
              barSize={12}
              data={chartData}
              startAngle={90}
              endAngle={90 - (normalizedRate * 3.6)} // 360 degrees for 100%
            >
              <RadialBar
                dataKey="value"
                cornerRadius={6}
                background={false}
              />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>

        {/* Center content */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              fontSize: size > 150 ? '32px' : '24px',
              fontWeight: 600,
              color: color,
              lineHeight: 1.2
            }}
          >
            {normalizedRate.toFixed(normalizedRate % 1 === 0 ? 0 : 1)}%
          </div>
          {showStatus && (
            <div
              style={{
                fontSize: '11px',
                color: token('color.text.subtle', '#6B778C'),
                marginTop: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {status}
            </div>
          )}
        </div>
      </div>

      {/* Label below gauge */}
      <div
        style={{
          marginTop: '8px',
          fontSize: '13px',
          fontWeight: 500,
          color: token('color.text', '#172B4D'),
          textAlign: 'center'
        }}
      >
        {label}
      </div>
    </div>
  )
}

export default PassRateGauge
